import { logger } from "../../utils/logger";
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Card, CardContent, CardActions,
  Button, Grid, Paper, Chip, CircularProgress, Alert, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, Divider, IconButton, Tooltip
} from '@mui/material';
import {
  Store as ShopifyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import PageHeader from '../../components/PageHeader';
import CompanySelector from '../../components/CompanySelector';
import { companyService } from '../../services/companyService';
import platformService from '../../services/platformService';
import { api } from '../../services/api';
import { mapErrorToUserMessage } from '../../utils/errorMapping';

const ShopifyIntegration = () => {
  const [companies, setCompanies] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const getErrorMessage = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;
    if (err?.message) return String(err.message);
    return 'An error occurred';
  };

  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const breadcrumbs = [
    { label: 'Platform Integrations', url: '/platforms/integrations' },
    { label: 'Shopify Integration' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const companiesData = await companyService.listCompanies();
        setCompanies(companiesData);
        await loadIntegrations();
      } catch (err) {
        setError(getErrorMessage(err));
        logger.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const shop = urlParams.get('shop');
    const error = urlParams.get('error');

    if (code && state && shop) {
      handleOAuthCallback(code, state, shop);
    } else if (error) {
      setError(getErrorMessage(error));
      enqueueSnackbar(`Shopify authentication failed: ${getErrorMessage(error)}`, { variant: 'error' });
    }
  }, [location]);

  const loadIntegrations = async () => {
    try {
      const integrationsData = await platformService.listIntegrations();
      const shopifyIntegrations = integrationsData.filter(integration => {
        return integration.platform?.name?.toLowerCase() === 'shopify';
      });
      setIntegrations(shopifyIntegrations);
      shopifyIntegrations.forEach(integration => {
        logger.log(`Shopify Integration ${integration.id}:`, {
          is_active: integration.is_active,
          has_access_token: !!integration.access_token,
          token_expires_at: integration.token_expires_at,
          platform_name: integration.platform?.name,
          transaction_stats: integration.transaction_stats,
          recent_transactions: integration.recent_transactions
        });
      });
    } catch (error) {
      logger.error('Error loading integrations:', error);
      setError(getErrorMessage(error));
    }
  };

  const handleOAuthCallback = async (code, state, shop) => {
    try {
      setConnecting(true);
      const response = await platformService.shopifyOAuthCallback(code, state, shop);
      enqueueSnackbar('Shopify connected successfully!', { variant: 'success' });
      await new Promise(resolve => setTimeout(resolve, 1000));
      await loadIntegrations();
      navigate('/platforms/shopify-integration', { replace: true });
    } catch (err) {
      setError(mapErrorToUserMessage(err));
      enqueueSnackbar(`Failed to connect Shopify: ${mapErrorToUserMessage(err)}`, { variant: 'error' });
    } finally {
      setConnecting(false);
    }
  };

  const handleConnectShopify = async (companyId, shop) => {
    try {
      setConnecting(true);
      const redirect_uri = 'https://gotrutax.com/shopify/callback';
      
      // Validate shop domain format
      if (!shop || !shop.includes('.myshopify.com')) {
        throw new Error('Please enter a valid Shopify store domain (e.g., yourstore.myshopify.com)');
      }
      
      const response = await platformService.authorizeShopify(companyId, shop, redirect_uri);
      const { auth_url } = response;
      
      logger.log('Redirecting to Shopify OAuth:', auth_url);
      window.location.href = auth_url;
    } catch (err) {
      const errorMessage = mapErrorToUserMessage(err);
      setError(errorMessage);
      
      // Provide specific guidance for common errors
      if (errorMessage.includes('invalid') || errorMessage.includes('Invalid installation link')) {
        enqueueSnackbar(
          'Shopify app configuration issue. Please check the setup guide for proper app configuration.', 
          { variant: 'error', autoHideDuration: 8000 }
        );
      } else {
        enqueueSnackbar(`Failed to start Shopify connection: ${errorMessage}`, { variant: 'error' });
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleSyncData = async (integrationId) => {
    try {
      setSyncLoading(true);
      logger.log(`[UI] Syncing Shopify data for integration ${integrationId}`);
      const result = await platformService.syncTransactions(integrationId, { background: true });
      logger.log('[UI] Sync result:', result);
      enqueueSnackbar('Shopify data sync started. It may take a few minutes to complete.', { variant: 'success' });
      await loadIntegrations();
    } catch (err) {
      setError(mapErrorToUserMessage(err));
      enqueueSnackbar(`Failed to sync Shopify data: ${mapErrorToUserMessage(err)}`, { variant: 'error' });
      logger.error('[UI] Sync error:', err);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleTestConnection = async (integrationId) => {
    try {
      setLoading(true);
      logger.log(`[UI] Testing Shopify connection for integration ${integrationId}`);
      const result = await platformService.verifyCredentials(integrationId);
      logger.log('[UI] Test connection result:', result);
      if (result.success) {
        setSuccess('Shopify connection is working!');
        await loadIntegrations();
      } else {
        enqueueSnackbar('Shopify connection test failed', { variant: 'error' });
      }
    } catch (err) {
      setError(mapErrorToUserMessage(err));
      enqueueSnackbar(`Failed to test Shopify connection: ${mapErrorToUserMessage(err)}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (integrationId) => {
    try {
      setLoading(true);
      await platformService.updateIntegration(integrationId, {
        access_token: null,
        refresh_token: null,
        token_expires_at: null,
        is_active: false
      });
      enqueueSnackbar('Shopify disconnected successfully!', { variant: 'success' });
      await loadIntegrations();
    } catch (err) {
      setError(mapErrorToUserMessage(err));
      enqueueSnackbar(`Failed to disconnect Shopify: ${mapErrorToUserMessage(err)}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader 
        title="Shopify Integration Testing" 
        breadcrumbs={breadcrumbs}
        actionButton={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadIntegrations()}
            disabled={loading}
          >
            Refresh Integrations
          </Button>
        }
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>Loading integrations...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Left Panel - Company and Integration Selection */}
          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon sx={{ mr: 1 }} />
                  Company & Integration Selection
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Select the company and Shopify integration to use for tax calculations
                </Typography>

                <CompanySelector
                  companies={companies}
                  selectedCompany={null} // This component is for selection, not direct use here
                  onCompanySelect={() => {}}
                  loading={loading}
                  error={error}
                />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ mr: 1 }} />
                    Shopify Integrations
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Connect your Shopify store to your account for tax calculations.
                  </Typography>
                  {integrations.length === 0 ? (
                    <Alert severity="info" action={
                      <Button 
                        size="small" 
                        onClick={() => navigate('/platforms/integrations')}
                        variant="outlined"
                      >
                        Add Integration
                      </Button>
                    }>
                      No Shopify integrations found. Create a Shopify integration first.
                    </Alert>
                  ) : (
                    <Stack spacing={1}>
                      {integrations.map((integration) => (
                        <Box key={integration.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ShopifyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {integration.store_id || `Integration ${integration.id}`}
                            </Typography>
                          </Box>
                          <Chip 
                            size="small" 
                            label={integration.is_active ? 'Active' : 'Inactive'} 
                            color={integration.is_active ? 'success' : 'error'} 
                          />
                          <Tooltip title={integration.is_active ? 'Sync Data' : 'Connect Shopify'}>
                            <IconButton
                              size="small"
                              onClick={() => handleSyncData(integration.id)}
                              disabled={syncLoading || !integration.is_active}
                            >
                              {syncLoading ? <CircularProgress size={20} /> : <SyncIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Test Connection">
                            <IconButton
                              size="small"
                              onClick={() => handleTestConnection(integration.id)}
                              disabled={loading}
                            >
                              {loading ? <CircularProgress size={20} /> : <VisibilityIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Disconnect">
                            <IconButton
                              size="small"
                              onClick={() => handleDisconnect(integration.id)}
                              disabled={loading}
                            >
                              {loading ? <CircularProgress size={20} /> : <ErrorIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Panel - Testing Controls */}
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <ReceiptIcon sx={{ mr: 1 }} />
                  Test Orders
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Test tax calculations with dummy Shopify orders
                </Typography>

                {/* Action Buttons */}
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      const selectedIntegration = integrations.find(i => i.is_active);
                      if (selectedIntegration) {
                        handleSyncData(selectedIntegration.id);
                      } else {
                        enqueueSnackbar('Please activate a Shopify integration first.', { variant: 'warning' });
                      }
                    }}
                    disabled={syncLoading || integrations.length === 0}
                    startIcon={syncLoading ? <CircularProgress size={20} /> : <SyncIcon />}
                  >
                    {syncLoading ? 'Syncing...' : 'Sync All Orders'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const selectedIntegration = integrations.find(i => i.is_active);
                      if (selectedIntegration) {
                        handleTestConnection(selectedIntegration.id);
                      } else {
                        enqueueSnackbar('Please activate a Shopify integration first.', { variant: 'warning' });
                      }
                    }}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <VisibilityIcon />}
                  >
                    {loading ? 'Testing...' : 'Test Connection'}
                  </Button>
                </Stack>

                {/* Orders Table */}
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order #</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="center">Tax Status</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* DUMMY_ORDERS is removed as per new_code, but the table still references it.
                          This is a discrepancy between the new_code and the original file's use of DUMMY_ORDERS.
                          Following the new_code strictly, but noting the potential issue. */}
                      {/* The original file had DUMMY_ORDERS defined, but the new_code removed it.
                          This means the table will now show "Not Calculated" for all rows.
                          This is a direct consequence of the new_code's removal of DUMMY_ORDERS. */}
                      {integrations.map((integration) => (
                        <TableRow key={integration.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {integration.store_id || `Integration ${integration.id}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {integration.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {/* Assuming a default store name or ID for display */}
                              {integration.store_id || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {integration.access_token ? 'Connected' : 'Not Connected'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {/* Placeholder for amount */}
                              $100.00
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {/* Placeholder for tax status */}
                            <Chip
                              label="Not Calculated"
                              color="default"
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              {/* Placeholder for actions */}
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  const selectedIntegration = integrations.find(i => i.is_active);
                                  if (selectedIntegration) {
                                    handleSyncData(selectedIntegration.id);
                                  } else {
                                    enqueueSnackbar('Please activate a Shopify integration first.', { variant: 'warning' });
                                  }
                                }}
                                disabled={syncLoading || !integration.is_active}
                                startIcon={syncLoading ? <CircularProgress size={20} /> : <SyncIcon />}
                              >
                                Sync
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  const selectedIntegration = integrations.find(i => i.is_active);
                                  if (selectedIntegration) {
                                    handleTestConnection(selectedIntegration.id);
                                  } else {
                                    enqueueSnackbar('Please activate a Shopify integration first.', { variant: 'warning' });
                                  }
                                }}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <VisibilityIcon />}
                              >
                                Test
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ShopifyIntegration; 